import { useMemo, useState } from "react";
import { Button, Checkbox, Modal, TextInput } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import { useMoney } from "@/shared/ui";
import { plateNameForLocale } from "@/modules/pos/lib/groupPlates";
import {
  markablePlates,
  partyShares,
  resolveAssignPartyPayload,
  unmarkedPlates,
  type AssignPartyInput,
} from "@/modules/pos/lib/partyShares";
import type { Invoice, InvoiceItem } from "@/lib/api/types";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";

/** A label long enough to name a family, short enough to fit a chip. */
export const PARTY_LABEL_MAX_LENGTH = 60;

/**
 * WHOSE PLATES ARE WHOSE (B43), at the till.
 *
 * The founder's case is a table of ten that is three families, each paying for
 * what it ordered. Until this screen the product's answer was "settle the whole
 * bill or don't", and the floor's workaround was three separate tables —
 * which distorts occupancy and average ticket exactly as the dummy-table
 * workaround did for takeaway.
 *
 * **The split is a MARKING, not an arithmetic.** A family does not owe "a
 * third"; it owes its plates, and the money falls out of the marking. So this
 * sheet has no amount field and never will: `assignParty` carries plate ids and
 * a label, and `settlePartial` with a `partyId` carries no figure at all. What
 * each family will be asked for is QUOTED here, from the same allocation the
 * server derives it with, so the cashier reads it before the command goes out
 * rather than off a toast afterwards.
 *
 * **Open OR closed.** The cheque printed and sitting on the table is exactly
 * when a family says "we're going, ours is the grill and the two colas", so the
 * server allows this while the bill is CLOSED and refuses it once SETTLED. The
 * money is in by then, and re-marking would move a share already taken.
 *
 * **THE UNCLAIMED PLATES ARE THE POINT.** The failure this screen exists to
 * prevent is a table leaving with one dish nobody paid for, so unmarked plates
 * are counted and named at the top — not quietly absorbed into the last family,
 * and not silently dropped. Unmarked is "nobody has been asked", exactly as a
 * null cover count is; it is never a default party.
 */
export function PartySheet({
  invoice,
  isSubmitting,
  onCancel,
  onConfirm,
}: {
  invoice: Invoice;
  isSubmitting: boolean;
  onCancel: () => void;
  /** Returns the payload built by `resolveAssignPartyPayload`, or nothing. */
  onConfirm: (input: AssignPartyInput) => void | Promise<void>;
}) {
  const t = useTranslate();
  const money = useMoney();
  /* Plate names in the language the sheet is IN — the exception the refund
     sheet already makes, and for the same reason: this is read aloud to a
     customer at the moment money changes hands, and an all-English list of
     «مشروب غازي» failed exactly there. */
  const { locale } = useI18n();

  const plates = useMemo(() => markablePlates(invoice), [invoice]);
  const shares = useMemo(() => partyShares(invoice), [invoice]);
  const unclaimed = useMemo(() => unmarkedPlates(invoice), [invoice]);

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  /*
   * WHICH PARTY the ticked plates go to. `null` is "a new one", named in the
   * field below; the `CLEAR` sentinel is the undo.
   *
   * DEF-7: nothing pre-selected. A pre-filled picker reads as already answered,
   * and the stale value goes out on the command log — which here would mark a
   * waiter's plates onto whichever family happened to be first.
   */
  const [target, setTarget] = useState<string | null | "CLEAR">(null);
  const [label, setLabel] = useState("");

  const selectedIds = plates
    .map((item) => item.id)
    .filter((id) => selected.has(id));

  const input: AssignPartyInput =
    target === "CLEAR"
      ? { mode: "clear", itemIds: selectedIds }
      : target === null
        ? { mode: "new", label, itemIds: selectedIds }
        : { mode: "existing", partyId: target, itemIds: selectedIds };

  // The SAME function that builds the wire payload decides whether Confirm may
  // be pressed — so "the button was enabled and the server refused" cannot
  // happen through a second, looser rule written in the component.
  const canConfirm = resolveAssignPartyPayload(input) !== null && !isSubmitting;

  const toggle = (itemId: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });

  /*
   * A marking in progress: ticked plates, or a family name half-typed — work a
   * swipe would throw away, and this sheet is used standing beside the table.
   * A destination picked with NOTHING ticked is not work (founder,
   * 2026-10-07): it is one tap to pick again, and asking "discard your
   * changes?" over it read as if closing would lose something.
   */
  const guard = useDiscardGuard(
    selectedIds.length > 0 || label.trim() !== "",
    onCancel,
  );

  return (
    <>
    <Modal
      isOpen
      onClose={guard.requestClose}
      size="sm"
      title={t((m) => m.pos.partyTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="party-sheet"
      footerActions={
        <>
          <Button
            variant="outline"
            size="touch"
            disabled={isSubmitting}
            onClick={guard.requestClose}
            data-testid="party-cancel"
          >
            {t((m) => m.common.cancel)}
          </Button>
          <Button
            /* PRIMARY, explicitly. Reading the round's screenshots found this
               button rendering as bare text beside a boxed Cancel — so the
               action that takes the money had less visual weight than the one
               that abandons it, at both widths and in both locales. The
               settle sheet beside it has always said `variant="primary"`. */
            variant="primary"
            size="touch"
            className="font-extrabold"
            disabled={!canConfirm}
            isLoading={isSubmitting}
            data-testid="party-confirm"
            onClick={() => {
              if (!canConfirm) return;
              void onConfirm(input);
              // The selection is the waiter's place in a list they are working
              // down; clearing it here is what makes "these three, then those
              // two" two taps rather than a re-scroll.
              setSelected(new Set());
              setLabel("");
            }}
          >
            {t((m) => m.pos.partyAssign)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* WHAT EACH FAMILY OWES SO FAR — the state of the split, before any
            edit. Quoted from the same allocation the server settles with, so a
            cashier can read the three figures and see they come to the bill. */}
        {shares.length > 0 ? (
          <ul className="flex flex-col gap-1" data-testid="party-summary">
            {shares.map((share) => (
              <li
                key={share.id}
                className="flex items-baseline justify-between gap-2 rounded-xl bg-[color:var(--surface-alt)] px-3 py-2"
                data-testid={`party-summary-${share.id}`}
              >
                <span className="min-w-0 truncate text-[length:calc(var(--font-size-xs)_+_0.5px)] font-bold">
                  {/* ISOLATED label, the row's own alignment — an Arabic family
                      name beside a Latin one must not drag its figure to the
                      other end of the row. */}
                  <bdi>{share.label}</bdi>
                </span>
                <span className="flex items-baseline gap-2 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
                  <span>
                    {/* The DUAL matters here: «2 أصناف» is as wrong in Arabic
                        as "1 bills" is in English, and the browser round found
                        exactly that on this row. `countedKey` owns the rule. */}
                    {t(
                      countedKey(share.itemCount, {
                        /* QA4 — ZERO is the ordinary first state of a family:
                           `partyShares` sets `itemCount: mine.length`, so a
                           family a waiter has just created and not yet marked
                           plates onto reads «0 صنف» until he does. */
                        none: (m) => m.pos.partyPlateCountNone,
                        one: (m) => m.pos.partyPlateCountOne,
                        two: (m) => m.pos.partyPlateCountTwo,
                        few: (m) => m.pos.partyPlateCountFew,
                        many: (m) => m.pos.partyPlateCount,
                      }),
                      { count: share.itemCount },
                    )}
                  </span>
                  <span className="tabular-nums font-extrabold text-[color:var(--text)]">
                    {share.amount === null ? "—" : money(share.amount)}
                  </span>
                  {/* WHETHER THIS FAMILY HAS PAID. Re-marking plates off a
                      family that has already handed money over moves a share
                      that is already in the drawer, and this row is where a
                      waiter decides to do it. Only on a family that has paid
                      something — a "paid 0.00" on every row would be noise on
                      the ordinary split. */}
                  {share.paid > 0 ? (
                    <span
                      className="font-extrabold text-[color:var(--success,var(--info))]"
                      data-testid={`party-summary-paid-${share.id}`}
                    >
                      {share.isSettled
                        ? t((m) => m.pos.partialPartyPaid)
                        : t((m) => m.pos.partialPartyPaidOf, {
                            paid: money(share.paid),
                            amount:
                              share.amount === null ? "—" : money(share.amount),
                          })}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {/* THE UNCLAIMED PLATES. Said in words and in the warning colour, and
            only when there are any — a "0 unclaimed" line on every split would
            teach a cashier to stop reading this block, which is the one line
            here that prevents a dish walking out unpaid.

            AND only once the bill has actually been split. On a bill nobody has
            marked, EVERY plate is unclaimed, so opening this sheet for the first
            time met an orange warning saying "3 plates are still nobody's" —
            an accusation about something the waiter had not done yet, on the
            screen where they were about to do it. A bill nobody has split is
            paid whole and has nothing to walk out unpaid. */}
        {unclaimed.length > 0 && shares.length > 0 ? (
          <p
            className="rounded-xl border border-[color:var(--warning)] px-3 py-2 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold text-[color:var(--warning)]"
            data-testid="party-unclaimed"
          >
            {t(
              countedKey(unclaimed.length, {
                one: (m) => m.pos.partyUnclaimedOne,
                two: (m) => m.pos.partyUnclaimedTwo,
                few: (m) => m.pos.partyUnclaimedFew,
                many: (m) => m.pos.partyUnclaimed,
              }),
              { count: unclaimed.length },
            )}
          </p>
        ) : null}

        <span className="text-xs font-bold text-[color:var(--text-muted)]">
          {t((m) => m.pos.partyPickPlates)}
        </span>

        <ul
          className="flex max-h-[32svh] flex-col gap-1 overflow-y-auto"
          data-testid="party-plate-list"
        >
          {plates.map((item) => (
            <li key={item.id}>
              <PlateRow
                item={item}
                name={plateNameForLocale(item, locale)}
                price={money(item.lineTotal)}
                partyLabel={
                  shares.find((share) => share.id === item.partyId)?.label ??
                  null
                }
                checked={selected.has(item.id)}
                onToggle={() => toggle(item.id)}
              />
            </li>
          ))}
        </ul>

        {/* WHERE THEY GO. Chips for the families that exist, one for a new one,
            one for the undo — the same control the tier picker uses, for the
            same reason: a short list picked on a touch screen mid-service. */}
        <div>
          <span className="mb-1.5 block text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
            {t((m) => m.pos.partyAssignTo)}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {shares.map((share) => (
              <TargetChip
                key={share.id}
                current={target === share.id}
                disabled={isSubmitting}
                testId={`party-target-${share.id}`}
                onClick={() => setTarget(share.id)}
              >
                <bdi>{share.label}</bdi>
              </TargetChip>
            ))}
            <TargetChip
              current={target === null}
              disabled={isSubmitting}
              testId="party-target-new"
              onClick={() => setTarget(null)}
            >
              {t((m) => m.pos.partyNew)}
            </TargetChip>
            {/* THE UNDO, beside the thing it undoes. A waiter who mis-taps a
                plate onto the wrong family must find the correction where they
                made the mistake, not in a different verb on a different
                screen. */}
            <TargetChip
              current={target === "CLEAR"}
              disabled={isSubmitting}
              testId="party-target-clear"
              onClick={() => setTarget("CLEAR")}
            >
              {t((m) => m.pos.partyClear)}
            </TargetChip>
          </div>
        </div>

        {target === null ? (
          <TextInput
            name="partyLabel"
            value={label}
            maxLength={PARTY_LABEL_MAX_LENGTH}
            // `dir="auto"` and not a forced direction: a family is as likely to
            // be «الستات» as "Ahmed's side", and the field has to read the way
            // the waiter types it.
            dir="auto"
            placeholder={t((m) => m.pos.partyLabelPlaceholder)}
            onChange={(event) => setLabel(event.target.value)}
            data-testid="party-label"
          />
        ) : null}

      </div>
    </Modal>
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="party-discard-prompt"
    />
    </>
  );
}

function PlateRow({
  item,
  name,
  price,
  partyLabel,
  checked,
  onToggle,
}: {
  item: InvoiceItem;
  name: string;
  price: string;
  /** Whose it currently is — null when nobody has been asked. */
  partyLabel: string | null;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      className={[
        "flex cursor-pointer items-start gap-2.5 rounded-xl border px-2.5 py-2 transition-colors",
        checked
          ? "border-[color:var(--accent)] bg-[color:var(--surface-alt)]"
          : "border-[color:var(--border-light)]",
      ].join(" ")}
    >
      <Checkbox
        checked={checked}
        onChange={onToggle}
        data-testid={`party-plate-${item.id}`}
        aria-label={name}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-semibold">{name}</span>
          <span className="tabular-nums text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold text-[color:var(--text-muted)]">
            {price}
          </span>
        </span>
        {/* WHOSE IT ALREADY IS, on the row. Without this the list is the same
            for a bill nobody has split and one split three ways, and a waiter
            re-marks a plate that was already right. */}
        {partyLabel ? (
          <span
            className="truncate text-[length:var(--font-size-2xs)] font-bold text-[color:var(--accent)]"
            data-testid={`party-plate-owner-${item.id}`}
          >
            <bdi>{partyLabel}</bdi>
          </span>
        ) : null}
      </span>
    </label>
  );
}

function TargetChip({
  current,
  disabled,
  testId,
  onClick,
  children,
}: {
  current: boolean;
  disabled: boolean;
  testId: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={current}
      disabled={disabled}
      data-testid={testId}
      onClick={onClick}
      className={[
        "rounded-[var(--radius-control)] border-[length:var(--border-panel-width,1px)] px-2 py-[5px] text-[length:var(--font-size-2xs)] font-extrabold transition-colors",
        current
          ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
          : "border-[color:var(--border-strong)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
